import { useEffect, useRef, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { LayoutDashboard } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import type { Session } from "@supabase/supabase-js";

interface Props {
  children: React.ReactNode;
}

export const RequireAdmin = ({ children }: Props) => {
  const [session, setSession] = useState<Session | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [authReady, setAuthReady] = useState(false);
  const checkedFor = useRef<string | null>(null);

  // 1) Set up listener (sync only) + get initial session.
  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setAuthReady(true);
    });

    return () => sub.subscription.unsubscribe();
  }, []);

  // 2) Check admin role once per user id.
  useEffect(() => {
    if (!authReady) return;
    if (!session?.user) {
      setIsAdmin(false);
      return;
    }
    const uid = session.user.id;
    if (checkedFor.current === uid) return;
    checkedFor.current = uid;

    (async () => {
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", uid)
        .eq("role", "admin")
        .maybeSingle();
      // On transient errors (e.g. 429), do not log the user out.
      if (error) {
        checkedFor.current = null;
        return;
      }
      setIsAdmin(!!data);
    })();
  }, [authReady, session?.user?.id]);

  if (!authReady || (session && isAdmin === null)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!session || isAdmin === false) {
    return <Navigate to="/admin/login" replace />;
  }

  const onDashboard = typeof window !== "undefined" && window.location.pathname === "/admin";
  return (
    <>
      {children}
      {!onDashboard && (
        <Link
          to="/admin"
          className="fixed bottom-5 left-5 z-50 inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground shadow-lg hover:opacity-90"
        >
          <LayoutDashboard className="w-4 h-4" />
          Dashboard
        </Link>
      )}
    </>
  );
};
